import { useCallback, useEffect, useState } from 'react';
import { Provider } from 'react-redux';

import { useUserHasAdvancesReporting } from 'utils/hooks/useUserHasAdvancedReporting';

import MainSidebar from './MainSidebar';

const hoverClass = 'main-nav-hover';

const MainSidebarWrapper = ({ navCollapsedPref, setCollapsed, store, viewerSocialProfile, ...props }) => {
    const [isCollapsed, setIsCollapsed] = useState(navCollapsedPref.get('value') === 'true');
    const [isHoverOpen, setIsHoverOpen] = useState(false);
    const [isMobileOpen, setIsMobileOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const { hasAdvancedReportingFeature, advancedReporting } = useUserHasAdvancesReporting({ viewerSocialProfile });

    // Since some manipulation of the class list is happening in Backbone, we have to listen for changes here.
    const addListenForCollapseChange = useCallback(() => {
        const body = document.querySelector('body');

        const fn = (mutationList, observer) => {
            // Start from the end so we only need to consider the most recent DOM mutation
            for (let i = mutationList.length - 1; i >= 0; i--) {
                if (mutationList[i].attributeName === 'class') {
                    setIsCollapsed(body.classList.contains('minimize-main-nav'));
                    setIsMobileOpen(body.classList.contains('mobile-open-main-nav'));
                    break;
                }
            }
        };

        const observer = new MutationObserver(fn);
        observer.observe(body, {
            attributes: true,
            childList: false,
            subtree: false
        });

        return observer;
    }, [setIsCollapsed, setIsMobileOpen]);

    const addHoverEffect = useCallback(() => {
        let hoverDelayTimer;
        const body = document.querySelector('body');
        const mainNavElement = document.querySelector('.main-nav');

        mainNavElement.addEventListener('mouseenter', () => {
            if (body.classList.contains('minimize-main-nav') && !body.classList.contains(hoverClass)) {
                hoverDelayTimer = setTimeout(() => {
                    body.classList.add(hoverClass);
                    setIsHoverOpen(true);
                }, 250);
            }
        });

        mainNavElement.addEventListener('mouseleave', () => {
            clearTimeout(hoverDelayTimer);

            if (body.classList.contains('minimize-main-nav') && body.classList.contains(hoverClass)) {
                body.classList.remove(hoverClass);
                setIsHoverOpen(false);
            }
        });
    }, [setIsHoverOpen]);

    const handleWindowResize = useCallback(() => {
        window.addEventListener('resize', function() {
            window.handleMobileView();

            if (window.innerWidth >= 768) {
                setIsMobile(false);
                setIsCollapsed(true);
            } else if (window.innerWidth < 768) {
                setIsMobile(true);
            }
        });

        // Recalculate the width of iframe pages and Highcharts
        // > Highcharts is already setup to listen for a window resize event
        // Golf Shot Map listens for resize event to correctly scale the map
        setTimeout(function() {
            window.dispatchEvent(new Event('resize.iframePage'));
            window.dispatchEvent(new Event('resize'));
        }, 0);
    }, []);

    useEffect(() => {
        addHoverEffect();
        handleWindowResize();
        const bodyMutationObserver = addListenForCollapseChange();

        return () => {
            window.removeEventListener('resize');
            document.querySelector('.main-nav').off('hover');
            bodyMutationObserver.disconnect();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Provider store={store}>
            <MainSidebar
                {...props}
                preferences={{
                    ...props.preferences,
                    isGCPEnabled: !!hasAdvancedReportingFeature
                }}
                advancedReporting={advancedReporting}
                isCollapsed={!isHoverOpen && (isMobile ? !isMobileOpen : isCollapsed)}
                setCollapsed={setCollapsed}
                isMobile={isMobile}
            />
        </Provider>
    );
};

export default MainSidebarWrapper;
